// frontend/src/utils/timeUtils.js

/**
 * Time utility functions for converting and formatting time values
 */

/**
 * Convert minutes to the most appropriate unit for display
 * @param {number} minutes - Time in minutes
 * @returns {object} - { value, unit }
 */
export function minutesToDisplayUnit(minutes) {
  if (minutes == null || minutes === '') {
    return { value: '', unit: 'minutes' };
  }

  const numMinutes = parseInt(minutes);

  // Convert to days if divisible by 1440 (24 hours)
  if (numMinutes >= 1440 && numMinutes % 1440 === 0) {
    return { value: numMinutes / 1440, unit: 'days' };
  }

  // Convert to hours if divisible by 60
  if (numMinutes >= 60 && numMinutes % 60 === 0) {
    return { value: numMinutes / 60, unit: 'hours' };
  }

  // Otherwise keep as minutes
  return { value: numMinutes, unit: 'minutes' };
}

/**
 * Convert display value and unit to minutes
 * @param {number} value - Display value
 * @param {string} unit - Unit (minutes, hours, days)
 * @returns {number} - Time in minutes
 */
export function displayUnitToMinutes(value, unit) {
  const numValue = parseInt(value) || 0;

  switch (unit) {
    case 'hours':
      return numValue * 60;
    case 'days':
      return numValue * 1440;
    case 'minutes':
    default:
      return numValue;
  }
}

/**
 * Get unit multiplier
 * @param {string} unit - Unit name
 * @returns {number} - Multiplier to convert to minutes
 */
export function getUnitMultiplier(unit) {
  switch (unit) {
    case 'hours':
      return 60;
    case 'days':
      return 1440;
    case 'minutes':
    default:
      return 1;
  }
}

/**
 * Format minutes for human-readable display
 * @param {number} minutes - Time in minutes
 * @param {function} t - Translation function
 * @returns {string} - Formatted string (e.g., "2 hours", "3 days")
 */
export function formatMinutes(minutes, t) {
  if (minutes == null || minutes === '') {
    return '';
  }

  const { value, unit } = minutesToDisplayUnit(minutes);

  // Use translation function if available
  if (t) {
    const unitKey = value === 1 ? unit.slice(0, -1) : unit; // singular/plural
    return `${value} ${t(unitKey)}`;
  }

  // Fallback without translation
  return `${value} ${unit}`;
}

/**
 * Format a range of minutes for display
 * @param {number} minMinutes - Minimum minutes
 * @param {number} maxMinutes - Maximum minutes
 * @param {function} t - Translation function
 * @returns {string} - Formatted range (e.g., "1 hour - 7 days")
 */
export function formatMinutesRange(minMinutes, maxMinutes, t) {
  const minFormatted = formatMinutes(minMinutes, t);
  const maxFormatted = formatMinutes(maxMinutes, t);
  return `${minFormatted} - ${maxFormatted}`;
}

/**
 * Get appropriate unit options based on min/max range
 * @param {number} minMinutes - Minimum allowed minutes
 * @param {number} maxMinutes - Maximum allowed minutes
 * @returns {array} - Array of unit options
 */
export function getApplicableUnits(minMinutes, maxMinutes) {
  const units = ['minutes'];

  // Add hours if range supports it (>= 60 minutes)
  if (maxMinutes >= 60) {
    units.push('hours');
  }

  // Add days if range supports it (>= 1440 minutes)
  if (maxMinutes >= 1440) {
    units.push('days');
  }

  return units;
}

/**
 * Validate if value is within allowed range after unit conversion
 * @param {number} value - Display value
 * @param {string} unit - Unit
 * @param {number} minMinutes - Minimum allowed minutes
 * @param {number} maxMinutes - Maximum allowed minutes
 * @returns {object} - { valid: boolean, minutes: number, message: string }
 */
export function validateTimeInput(value, unit, minMinutes, maxMinutes) {
  const minutes = displayUnitToMinutes(value, unit);

  if (minutes < minMinutes) {
    return {
      valid: false,
      minutes,
      message: `Minimum is ${formatMinutes(minMinutes)}`
    };
  }

  if (minutes > maxMinutes) {
    return {
      valid: false,
      minutes,
      message: `Maximum is ${formatMinutes(maxMinutes)}`
    };
  }

  return {
    valid: true,
    minutes
  };
}
